import { format, isValid, parseISO, subDays } from "date-fns";
import type { TFunction } from "i18next";
import qs from "qs";

import { APIFilter } from "shared/api/utils";
import { roundToNDecimals } from "shared/utils";

import {
  ASSETS_PAGE_KEY as REPAIR_LABOR_ASSETS_PAGE_KEY,
  PAGE_KEY as REPAIR_LABOR_PAGE_KEY_V2,
} from "pages/RepairLabor/v2/constants";
import {
  ASSETS_PAGE_KEY as REPAIR_PARTS_ASSETS_PAGE_KEY,
  PAGE_KEY as REPAIR_PARTS_PAGE_KEY_V2,
} from "pages/RepairParts/v2/constants";
import {
  ASSETS_PAGE_KEY as REPAIRS_ASSETS_PAGE_KEY,
  PAGE_KEY as REPAIRS_PAGE_KEY_V2,
} from "pages/Repairs/v2/constants";

import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";
import { getFiltersKey } from "features/ui/Filters/utils";
import LoadingIcon from "features/ui/Icons/LoadingIcon";

import { routes } from "services/routes";

import { RepairRecommendation, ServiceCenter, VehicleData } from "./types";

export const hasUsableRange = (
  minimum: number | null | undefined,
  maximum: number | null | undefined
) =>
  typeof minimum === "number" &&
  typeof maximum === "number" &&
  Number.isFinite(minimum) &&
  Number.isFinite(maximum) &&
  maximum - minimum > 0;

export const useRepairLaborLink = (
  repairLaborFilterQuery: string,
  assetFilterQuery: string
) => {
  const queryParams = {
    [getFiltersKey(REPAIR_LABOR_PAGE_KEY_V2)]: repairLaborFilterQuery,
    [getFiltersKey(REPAIR_LABOR_ASSETS_PAGE_KEY)]: assetFilterQuery,
    filtersExpanded: "false",
  };

  return `${routes.repairLabor}?${qs.stringify(queryParams, { arrayFormat: "indices" })}`;
};

export const useRepairPartsLink = (
  repairPartsFilterQuery: string,
  assetFilterQuery: string
) => {
  const queryParams = {
    [getFiltersKey(REPAIR_PARTS_PAGE_KEY_V2)]: repairPartsFilterQuery,
    [getFiltersKey(REPAIR_PARTS_ASSETS_PAGE_KEY)]: assetFilterQuery,
    filtersExpanded: "false",
  };

  return `${routes.repairParts}?${qs.stringify(queryParams, { arrayFormat: "indices" })}`;
};

export const useRepairsLink = (
  repairFilterQuery: string,
  assetFilterQuery: string
) => {
  const queryParams = {
    [getFiltersKey(REPAIRS_PAGE_KEY_V2)]: repairFilterQuery,
    [getFiltersKey(REPAIRS_ASSETS_PAGE_KEY)]: assetFilterQuery,
    filtersExpanded: "false",
  };

  return `${routes.repairs}?${qs.stringify(queryParams, { arrayFormat: "indices" })}`;
};

export const hasLabor = (repairRecommendation: RepairRecommendation): boolean =>
  repairRecommendation.labor_recommendations.length > 0;

export const hasParts = (repairRecommendation: RepairRecommendation): boolean =>
  repairRecommendation.part_recommendations.length > 0;

const hasOther = (repairRecommendation: RepairRecommendation): boolean =>
  repairRecommendation.other_recommendation !== null;

export const hasBothOtherAndLaborOrParts = (
  repairRecommendation: RepairRecommendation
): boolean =>
  hasOther(repairRecommendation) && hasPartsOrLabor(repairRecommendation);

const hasPartsOrLabor = (repairRecommendation: RepairRecommendation): boolean =>
  repairRecommendation.part_recommendations.length > 0 ||
  repairRecommendation.labor_recommendations.length > 0;

export const hasPriceAnalysis = (
  repairRecommendation: RepairRecommendation
): boolean =>
  hasPartsOrLabor(repairRecommendation) || hasOther(repairRecommendation);

export const buildAssetFilters = (vehicleData: VehicleData) => {
  const filter = [];
  if (vehicleData.make) {
    filter.push(`make=in:${vehicleData.make}`);
  } else {
    filter.push(`make=is:null`);
  }

  if (vehicleData.model) {
    filter.push(`model=in:${vehicleData.model}`);
  } else {
    filter.push(`model=is:null`);
  }

  if (vehicleData.year) {
    filter.push(
      `modelYear=in:${vehicleData.year}|${vehicleData.year - 1}|${vehicleData.year + 1}`
    );
  } else {
    filter.push(`modelYear=is:null`);
  }

  return filter.join(",");
};

export const subtractDaysAndFormat = (dateStr: string, days: number) => {
  const date = parseISO(dateStr);

  if (!isValid(date)) {
    return "";
  }

  const newDate = subDays(date, days);

  return format(newDate, "yyyy-MM-dd");
};

export const buildRepairPartsFilter = (
  vmrs_id: string,
  repair_id: string,
  quantity: number,
  work_order_date: string,
  work_order_id: string,
  considered_history_days: number,
  min_threshold_cost_parts: number | null,
  max_threshold_cost_parts: number | null,
  brand_name?: string
): APIFilter[] =>
  [
    { name: "VMRSID", value: vmrs_id, op: "in" },
    { name: "repairID", value: repair_id, op: "nin" },
    {
      name: "costPerUnit",
      value: [min_threshold_cost_parts || 0],
      op: "gt",
    },
    max_threshold_cost_parts && {
      name: "costPerUnit",
      value: [max_threshold_cost_parts],
      op: "lte",
    },
    { name: "quantity", value: [0], op: "gt" },
    { name: "quantity", value: quantity, op: "lte" },
    {
      name: "repair.workOrderDate",
      value: [
        subtractDaysAndFormat(work_order_date, considered_history_days),
        work_order_date,
      ],
      op: "between",
    },
    {
      name: "repair.workOrderID",
      value: work_order_id,
      op: "nin",
    },
    brand_name && {
      name: "repair.serviceCenterName",
      value: brand_name,
      op: "like",
    },
  ].filter(Boolean) as APIFilter[];

export const buildRepairLaborFilter = (
  vmrs_id: string,
  repair_id: string,
  quantity: number,
  work_order_date: string,
  work_order_id: string,
  considered_history_days: number,
  min_threshold_cost_labor: number | null,
  max_threshold_cost_labor: number | null,
  brand_name?: string
): APIFilter[] =>
  [
    { name: "VMRSID", value: vmrs_id, op: "in" },
    { name: "repairID", value: repair_id, op: "nin" },
    {
      name: "costPerUnit",
      value: [min_threshold_cost_labor || 0],
      op: "gt",
    },
    max_threshold_cost_labor && {
      name: "costPerUnit",
      value: [max_threshold_cost_labor],
      op: "lte",
    },
    { name: "quantity", value: [0], op: "gt" },
    { name: "quantity", value: quantity, op: "lte" },
    {
      name: "repair.workOrderDate",
      value: [
        subtractDaysAndFormat(work_order_date, considered_history_days),
        work_order_date,
      ],
      op: "between",
    },
    {
      name: "repair.workOrderID",
      value: work_order_id,
      op: "nin",
    },
    brand_name && {
      name: "repair.serviceCenterName",
      value: brand_name,
      op: "like",
    },
  ].filter(Boolean) as APIFilter[];

export const buildRepairOtherFilter = ({
  part_vmrs_ids = [],
  labor_vmrs_ids = [],
  description,
  work_order_date,
  work_order_id,
  considered_history_days,
  min_threshold_cost_other,
  max_threshold_cost_other,
  brand_name,
}: {
  part_vmrs_ids?: string[];
  labor_vmrs_ids?: string[];
  description?: string | null;
  work_order_date: string;
  work_order_id: string;
  considered_history_days: number;
  min_threshold_cost_other: number | null;
  max_threshold_cost_other: number | null;
  brand_name?: string;
}): APIFilter[] => {
  const normalized_part_vmrs_ids = [...new Set(part_vmrs_ids.filter(Boolean))];
  const normalized_labor_vmrs_ids = [
    ...new Set(labor_vmrs_ids.filter(Boolean)),
  ];
  const normalizedDescription = description?.trim();

  const matchingFilters =
    normalized_part_vmrs_ids.length > 0
      ? normalized_part_vmrs_ids.map((vmrs_id) => ({
          name: "parts",
          value: `[${getFiltersQuery(undefined, [
            {
              name: "VMRSID",
              value: vmrs_id,
              op: "in",
            },
          ])}]`,
          op: "exists" as const,
        }))
      : normalized_labor_vmrs_ids.length > 0
        ? normalized_labor_vmrs_ids.map((vmrs_id) => ({
            name: "labor",
            value: `[${getFiltersQuery(undefined, [
              {
                name: "VMRSID",
                value: vmrs_id,
                op: "in",
              },
            ])}]`,
            op: "exists" as const,
          }))
        : normalizedDescription
          ? [
              {
                name: "description",
                value: normalizedDescription,
                op: "in" as const,
              },
            ]
          : [];

  return [
    min_threshold_cost_other !== null && {
      name: "costOther",
      value: [min_threshold_cost_other || 0],
      op: "gt",
    },
    max_threshold_cost_other !== null && {
      name: "costOther",
      value: [max_threshold_cost_other],
      op: "lte",
    },
    {
      name: "workOrderDate",
      value: [
        subtractDaysAndFormat(work_order_date, considered_history_days),
        work_order_date,
      ],
      op: "between",
    },
    { name: "workOrderID", value: work_order_id, op: "nin" },
    ...matchingFilters,
    brand_name && { name: "serviceCenterName", value: brand_name, op: "like" },
  ].filter(Boolean) as APIFilter[];
};

export const getVendorLocation = (
  serviceCentersByID: Record<string, ServiceCenter>,
  serviceCenterCurrent: string,
  t: TFunction,
  serviceCenterID?: string | null
) => {
  if (!serviceCenterID) {
    return t("duck:price_advisor_includes_non_local_vendors");
  }

  if (serviceCenterID === serviceCenterCurrent) {
    return <b>{t("duck:price_advisor_current_vendor")}</b>;
  }

  if (!serviceCentersByID[serviceCenterID]) {
    return t("duck:price_advisor_unknown_location");
  }

  return (
    serviceCentersByID[serviceCenterID].address +
    (serviceCentersByID[serviceCenterID].distance_to_current_service_center !==
    null
      ? t("duck:price_advisor_vendor_distance", {
          distance: roundToNDecimals(
            serviceCentersByID[serviceCenterID]
              ?.distance_to_current_service_center!,
            1
          ),
        })
      : "")
  );
};

export const getVendorName = (
  serviceCentersByID: Record<string, ServiceCenter>,
  t: TFunction,
  serviceCenterID?: string | null,
  isStreaming?: boolean
) => {
  if (!serviceCenterID) {
    return t("duck:price_advisor_all_service_locations");
  }

  if (!serviceCentersByID[serviceCenterID]) {
    return isStreaming ? (
      <LoadingIcon />
    ) : (
      t("duck:price_advisor_unknown_vendor")
    );
  }

  return serviceCentersByID[serviceCenterID]?.name;
};
